/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

export function getApprovalEntity(notification: any, entityType: string): any {
  return _.get(notification, entityType) || {};
}

export function getApprovalCreative(notification: any): any {
  return _.has(notification, 'creative') ? _.get(notification, 'creative') : notification;
}

export function getApprovalHistoryEntityType(historyItem: any): string | null {
  if (_.has(historyItem, 'creative')) {
    return 'creative';
  }
  if (_.has(historyItem, 'lineItem')) {
    return 'lineItem';
  }
  if (_.has(historyItem, 'order')) {
    return 'order';
  }
  if (_.has(historyItem, 'advertiser')) {
    return 'advertiser';
  }
  if (_.has(historyItem, 'team')) {
    return 'team';
  }
  return null;
}

export const APPROVAL_HISTORY_SEARCH_TYPES = ['Advertiser', 'Team', 'LineItem', 'Order', 'Creative'];

const APPROVAL_HISTORY_FILTER_FIELDS: Record<string, string> = {
  Advertiser: 'advertiser',
  advertiser: 'advertiser',
  Team: 'team',
  team: 'team',
  LineItem: 'lineItem',
  lineItem: 'lineItem',
  Order: 'order',
  order: 'order',
  Creative: 'creative',
  creative: 'creative'
};

/**
 * POST body for /earningsaccounts/{id}/approvalhistory. The API resolves parents
 * from the selected object, so only that object's reference is sent.
 */
export function getApprovalHistoryFilterRequest(selected: any): any {
  if (!selected) {
    return {};
  }
  const typeKey = _.get(selected, ['objectType', 'apiParam'])
    || _.get(selected, ['objectType', 'type'])
    || selected.type;
  const field = APPROVAL_HISTORY_FILTER_FIELDS[typeKey];
  const id = selected.id;
  if (!field || !id) {
    return {};
  }
  return {[field]: {id}};
}

export function getApprovalHistorySearchLabel(result: any): string {
  const typeLabel = result?.typeLabel || _.get(result, ['objectType', 'name']) || result?.type;
  const name = result?.name || result?.id || '';
  if (!typeLabel) {
    return name;
  }
  return typeLabel + ': ' + name;
}

export function getApprovalEntityKey(notification: any, entityType: string, parentKey: string): string {
  const entity = getApprovalEntity(notification, entityType);
  return parentKey + ':' + entityType + ':' + (_.get(entity, 'id') || _.get(entity, 'name') || 'unknown');
}

export function getExpandedApprovalNotificationGroups(approvalNotificationTree: any[]): Record<string, boolean> {
  const expandedGroups: Record<string, boolean> = {};

  function addExpandedGroups(groups: any[]): void {
    _.each(groups || [], (group) => {
      if (group.expanded) {
        expandedGroups[group.key] = true;
      }
      addExpandedGroups(group.children);
    });
  }

  addExpandedGroups(approvalNotificationTree);
  return expandedGroups;
}

export function createApprovalNotificationGroup(type: string, key: string, entity: any, expandedGroups: Record<string, boolean>): any {
  return {
    type,
    key,
    entity: entity || {},
    children: [],
    childMap: {},
    expanded: expandedGroups[key] === true,
    advertiserCount: 0,
    orderCount: 0,
    lineItemCount: 0,
    creativeCount: 0
  };
}

export function getOrCreateApprovalNotificationGroup(parent: any, type: string, key: string, entity: any, expandedGroups: Record<string, boolean>): any {
  if (!parent.childMap[key]) {
    parent.childMap[key] = createApprovalNotificationGroup(type, key, entity, expandedGroups);
    parent.children.push(parent.childMap[key]);
  }
  return parent.childMap[key];
}

export function updateApprovalNotificationCounts(teams: any[]): void {
  _.each(teams, (team) => {
    team.advertiserCount = team.children.length;
    team.orderCount = 0;
    team.lineItemCount = 0;
    team.creativeCount = 0;
    team.acknowledgeableCreativeCount = 0;

    _.each(team.children, (advertiser) => {
      advertiser.orderCount = advertiser.children.length;
      advertiser.lineItemCount = 0;
      advertiser.creativeCount = 0;
      advertiser.acknowledgeableCreativeCount = 0;

      _.each(advertiser.children, (order) => {
        order.lineItemCount = order.children.length;
        order.creativeCount = 0;
        order.acknowledgeableCreativeCount = 0;

        _.each(order.children, (lineItem) => {
          lineItem.creativeCount = lineItem.children.length;
          lineItem.acknowledgeableCreativeCount = _(lineItem.children).map((creativeRow) => {
            return _.get(creativeRow, 'creative.id');
          }).compact().uniq().value().length;
          order.creativeCount += lineItem.creativeCount;
          order.acknowledgeableCreativeCount += lineItem.acknowledgeableCreativeCount;
        });

        advertiser.lineItemCount += order.lineItemCount;
        advertiser.creativeCount += order.creativeCount;
        advertiser.acknowledgeableCreativeCount += order.acknowledgeableCreativeCount;
      });

      team.orderCount += advertiser.orderCount;
      team.lineItemCount += advertiser.lineItemCount;
      team.creativeCount += advertiser.creativeCount;
      team.acknowledgeableCreativeCount += advertiser.acknowledgeableCreativeCount;
    });
  });
}

export function getApprovalNotificationsForRow(row: any): any[] {
  const notifications: any[] = [];

  function addNotifications(node: any): void {
    if (!node) {
      return;
    }
    if (node.notification) {
      notifications.push(node.notification);
    }
    _.each(node.children, addNotifications);
  }

  if (row && row.notification) {
    return [row.notification];
  }

  addNotifications(_.get(row, 'group'));
  return notifications;
}

export function getActionableApprovalCreatives(notifications: any[]): any[] {
  return _(notifications).map((notification) => {
    return getApprovalCreative(notification);
  }).filter((creative) => {
    return creative && creative.id;
  }).uniqBy('id').value();
}

export function setApprovalNotificationRowAction(row: any, creatives: any[], action: string, active: boolean): void {
  if (row) {
    row[action] = active;
  }
  _.each(creatives, (creative) => {
    creative[action] = active;
  });
}

export function setApprovalNotificationRowsAction(rows: any[], action: string, active: boolean): void {
  _.each(rows || [], (row) => {
    const creative = _.get(row, 'creative');
    if (row) {
      row[action] = active;
    }
    if (creative) {
      creative[action] = active;
    }
  });
}

export function getApprovalPayload(type: string, targetId: string, approval: string): any {
  if (!targetId) {
    return null;
  }
  const payload: any = {approval};
  if (type === 'team') {
    payload.approvedTeam = targetId;
  } else {
    payload[type] = targetId;
  }
  return payload;
}

export function getApprovalPayloadForRow(row: any, approval: string): any {
  const approvalTargets = {
    team: row && row.team,
    advertiser: row && row.advertiser,
    order: row && row.order,
    lineItem: row && row.lineItem,
    creative: row && row.creative
  };
  const target = approvalTargets[_.get(row, 'type') as keyof typeof approvalTargets];
  return getApprovalPayload(_.get(row, 'type'), _.get(target, 'id'), approval);
}

export function getApprovalPayloadForHistoryItem(historyItem: any, approval: string): any {
  const entityType = _.get(historyItem, 'entityType') || getApprovalHistoryEntityType(historyItem);
  return getApprovalPayload(entityType as string, _.get(historyItem, [entityType as string, 'id']), approval);
}

export function getApprovalPreviewHistoryQuery(historyItem: any): any {
  const entityType = _.get(historyItem, 'entityType') || getApprovalHistoryEntityType(historyItem);

  if (entityType === 'creative' && _.get(historyItem, 'creative.id')) {
    return null;
  }
  if (entityType === 'lineItem' && _.get(historyItem, 'lineItem.id')) {
    return {lineItem: _.get(historyItem, 'lineItem.id'), concise: true, pageSize: 201};
  }
  if (entityType === 'order' && _.get(historyItem, 'order.id')) {
    return {order: _.get(historyItem, 'order.id'), concise: true, pageSize: 201};
  }
  if (entityType === 'advertiser' && _.get(historyItem, 'advertiser.id')) {
    return {advertiser: _.get(historyItem, 'advertiser.id'), concise: true, pageSize: 201};
  }
  if (entityType === 'team' && _.get(historyItem, 'team.id')) {
    return {team: _.get(historyItem, 'team.id'), concise: true, pageSize: 201};
  }
  return null;
}

export function buildApprovalPreviewRowFromHistoryItem(historyItem: any, creatives?: any[]): any {
  const entityType = _.get(historyItem, 'entityType') || getApprovalHistoryEntityType(historyItem);
  const previewCreatives = creatives || [];
  const entity = _.get(historyItem, entityType as string) || {};

  return {
    type: entityType,
    team: historyItem.team,
    advertiser: historyItem.advertiser,
    order: historyItem.order,
    lineItem: historyItem.lineItem,
    creative: historyItem.creative,
    approval: historyItem.approval,
    fromHistory: true,
    acknowledgeableCreativeCount: previewCreatives.length || (_.get(historyItem, 'creative.id') ? 1 : 0),
    approvable: !!_.get(entity, 'id')
  };
}

export function buildApprovalPreviewNotificationsFromHistoryItem(historyItem: any, creatives?: any[]): any[] {
  const previewCreatives = creatives && creatives.length ? creatives : (_.get(historyItem, 'creative.id') ? [historyItem.creative] : []);

  return _.map(previewCreatives, (creative) => {
    return {
      team: historyItem.team,
      advertiser: historyItem.advertiser,
      order: historyItem.order,
      lineItem: historyItem.lineItem,
      creative,
      approval: _.get(creative, 'approval') || historyItem.approval
    };
  });
}

export function getApprovalPreviewRow(row: any): any {
  if (row && row.entityType && !row.type && _.get(row, 'creative.id')) {
    return {
      type: 'creative',
      team: row.team,
      advertiser: row.advertiser,
      order: row.order,
      lineItem: row.lineItem,
      creative: row.creative,
      approval: row.approval,
      fromHistory: true,
      notification: {
        team: row.team,
        advertiser: row.advertiser,
        order: row.order,
        lineItem: row.lineItem,
        creative: row.creative,
        approval: row.approval
      },
      acknowledgeableCreativeCount: 1,
      approvable: true
    };
  }
  return row;
}

export function buildApprovalNotificationRow(type: string, group: any, parentEntities: any): any {
  const team = _.get(parentEntities, 'team');
  const advertiser = _.get(parentEntities, 'advertiser');
  const order = _.get(parentEntities, 'order');
  const lineItem = _.get(parentEntities, 'lineItem');

  if (type === 'team') {
    return {
      type: 'team',
      key: group.key,
      group,
      team: group.entity,
      advertiserCount: group.advertiserCount,
      orderCount: group.orderCount,
      lineItemCount: group.lineItemCount,
      creativeCount: group.creativeCount,
      acknowledgeableCreativeCount: group.acknowledgeableCreativeCount,
      approvable: !!_.get(group, 'entity.id')
    };
  }
  if (type === 'advertiser') {
    return {
      type: 'advertiser',
      key: group.key,
      group,
      team,
      advertiser: group.entity,
      orderCount: group.orderCount,
      lineItemCount: group.lineItemCount,
      creativeCount: group.creativeCount,
      acknowledgeableCreativeCount: group.acknowledgeableCreativeCount,
      approvable: !!_.get(group, 'entity.id')
    };
  }
  if (type === 'order') {
    return {
      type: 'order',
      key: group.key,
      group,
      team,
      advertiser,
      order: group.entity,
      lineItemCount: group.lineItemCount,
      creativeCount: group.creativeCount,
      acknowledgeableCreativeCount: group.acknowledgeableCreativeCount,
      approvable: !!_.get(group, 'entity.id')
    };
  }
  if (type === 'lineItem') {
    return {
      type: 'lineItem',
      key: group.key,
      group,
      team,
      advertiser,
      order,
      lineItem: group.entity,
      creativeCount: group.creativeCount,
      acknowledgeableCreativeCount: group.acknowledgeableCreativeCount,
      approvable: !!_.get(group, 'entity.id')
    };
  }
  return {
    type: 'creative',
    key: group.key,
    team,
    advertiser,
    order,
    lineItem,
    creative: group.creative,
    notification: group.notification,
    acknowledgeableCreativeCount: _.get(group, 'creative.id') ? 1 : 0,
    approvable: !!_.get(group, 'creative.id')
  };
}

export function decorateApprovalNotificationTree(approvalNotificationTree: any[]): void {
  _.each(approvalNotificationTree, (team) => {
    team.row = buildApprovalNotificationRow('team', team, {});

    _.each(team.children, (advertiser) => {
      advertiser.row = buildApprovalNotificationRow('advertiser', advertiser, {team: team.entity});

      _.each(advertiser.children, (order) => {
        order.row = buildApprovalNotificationRow('order', order, {
          team: team.entity,
          advertiser: advertiser.entity
        });

        _.each(order.children, (lineItem) => {
          lineItem.row = buildApprovalNotificationRow('lineItem', lineItem, {
            team: team.entity,
            advertiser: advertiser.entity,
            order: order.entity
          });

          _.each(lineItem.children, (creativeRow) => {
            creativeRow.row = buildApprovalNotificationRow('creative', creativeRow, {
              team: team.entity,
              advertiser: advertiser.entity,
              order: order.entity,
              lineItem: lineItem.entity
            });
          });
        });
      });
    });
  });
}

export function refreshApprovalNotificationRows(approvalNotificationTree: any[]): any[] {
  const rows: any[] = [];

  _.each(approvalNotificationTree, (team) => {
    rows.push({
      type: 'team',
      key: team.key,
      group: team,
      team: team.entity,
      advertiserCount: team.advertiserCount,
      orderCount: team.orderCount,
      lineItemCount: team.lineItemCount,
      creativeCount: team.creativeCount,
      acknowledgeableCreativeCount: team.acknowledgeableCreativeCount,
      approvable: !!_.get(team, 'entity.id')
    });

    if (!team.expanded) {
      return;
    }

    _.each(team.children, (advertiser) => {
      rows.push({
        type: 'advertiser',
        key: advertiser.key,
        group: advertiser,
        team: team.entity,
        advertiser: advertiser.entity,
        orderCount: advertiser.orderCount,
        lineItemCount: advertiser.lineItemCount,
        creativeCount: advertiser.creativeCount,
        acknowledgeableCreativeCount: advertiser.acknowledgeableCreativeCount,
        approvable: !!_.get(advertiser, 'entity.id')
      });

      if (!advertiser.expanded) {
        return;
      }

      _.each(advertiser.children, (order) => {
        rows.push({
          type: 'order',
          key: order.key,
          group: order,
          team: team.entity,
          advertiser: advertiser.entity,
          order: order.entity,
          lineItemCount: order.lineItemCount,
          creativeCount: order.creativeCount,
          acknowledgeableCreativeCount: order.acknowledgeableCreativeCount,
          approvable: !!_.get(order, 'entity.id')
        });

        if (!order.expanded) {
          return;
        }

        _.each(order.children, (lineItem) => {
          rows.push({
            type: 'lineItem',
            key: lineItem.key,
            group: lineItem,
            team: team.entity,
            advertiser: advertiser.entity,
            order: order.entity,
            lineItem: lineItem.entity,
            creativeCount: lineItem.creativeCount,
            acknowledgeableCreativeCount: lineItem.acknowledgeableCreativeCount,
            approvable: !!_.get(lineItem, 'entity.id')
          });

          if (!lineItem.expanded) {
            return;
          }

          _.each(lineItem.children, (creativeRow) => {
            rows.push({
              type: 'creative',
              key: creativeRow.key,
              team: team.entity,
              advertiser: advertiser.entity,
              order: order.entity,
              lineItem: lineItem.entity,
              creative: creativeRow.creative,
              notification: creativeRow.notification,
              acknowledgeableCreativeCount: _.get(creativeRow, 'creative.id') ? 1 : 0,
              approvable: !!_.get(creativeRow, 'creative.id')
            });
          });
        });
      });
    });
  });

  return rows;
}

export function buildApprovalNotificationTree(approvalNotifications: any[], approvalNotificationTree: any[]): any[] {
  const expandedGroups = getExpandedApprovalNotificationGroups(approvalNotificationTree);
  const root = {
    children: [] as any[],
    childMap: {} as Record<string, any>
  };

  _.each(approvalNotifications, (notification, index) => {
    const teamKey = getApprovalEntityKey(notification, 'team', 'approval');
    const team = getOrCreateApprovalNotificationGroup(root, 'team', teamKey, getApprovalEntity(notification, 'team'), expandedGroups);
    const advertiserKey = getApprovalEntityKey(notification, 'advertiser', teamKey);
    const advertiser = getOrCreateApprovalNotificationGroup(team, 'advertiser', advertiserKey, getApprovalEntity(notification, 'advertiser'), expandedGroups);
    const orderKey = getApprovalEntityKey(notification, 'order', advertiserKey);
    const order = getOrCreateApprovalNotificationGroup(advertiser, 'order', orderKey, getApprovalEntity(notification, 'order'), expandedGroups);
    const lineItemKey = getApprovalEntityKey(notification, 'lineItem', orderKey);
    const lineItem = getOrCreateApprovalNotificationGroup(order, 'lineItem', lineItemKey, getApprovalEntity(notification, 'lineItem'), expandedGroups);
    const creative = getApprovalCreative(notification);
    const creativeKey = lineItemKey + ':creative:' + (_.get(creative, 'id') || _.get(creative, 'name') || 'unknown') + ':' + index;

    lineItem.children.push({
      type: 'creative',
      key: creativeKey,
      creative: creative || {},
      notification
    });
  });

  updateApprovalNotificationCounts(root.children);
  const tree = root.children;
  decorateApprovalNotificationTree(tree);
  return tree;
}

export function canPreviewApprovalHistoryItem(historyItem: any): boolean {
  const entityType = _.get(historyItem, 'entityType') || getApprovalHistoryEntityType(historyItem);
  if (entityType === 'creative') {
    return !!_.get(historyItem, 'creative.id');
  }
  if (entityType === 'lineItem') {
    return !!_.get(historyItem, 'lineItem.id');
  }
  if (entityType === 'order') {
    return !!_.get(historyItem, 'order.id');
  }
  if (entityType === 'advertiser') {
    return !!_.get(historyItem, 'advertiser.id');
  }
  if (entityType === 'team') {
    return !!_.get(historyItem, 'team.id');
  }
  return false;
}

export function getApprovalHistoryApprovalLabel(approval: string): string {
  if (approval === 'APPROVED') {
    return 'Approved';
  }
  if (approval === 'BLOCKED') {
    return 'Blocked';
  }
  if (approval === 'PENDING') {
    return 'Pending';
  }
  return approval;
}

export function getApprovalHistoryApprovalClass(approval: string): string {
  if (approval === 'APPROVED') {
    return 'label-success';
  }
  if (approval === 'BLOCKED') {
    return 'label-danger';
  }
  if (approval === 'PENDING') {
    return 'label-primary';
  }
  return 'label-default';
}

export function getOppositeApprovalForHistoryItem(historyItem: any): string | null {
  if (_.get(historyItem, 'approval') === 'APPROVED') {
    return 'BLOCKED';
  }
  if (_.get(historyItem, 'approval') === 'BLOCKED') {
    return 'APPROVED';
  }
  return null;
}
